import axios from 'axios';
import { GA_MEASUREMENT_ID } from 'constants/env';
import { generatePath } from 'react-router-dom';

import { API_REGISTRATION_STATUS, API_URL } from 'src/constants';

import { onUserFieldChange } from 'components/form/userChangeListeners';

import { APP_ERROR_EVENT } from 'utils/appErrorEvent';
import { appEnvControl, history, historyStore } from 'utils';
import { onRequestError, RequestErrorInfo } from 'utils/requestErrorListeners';

import { form, sessionStore } from 'stores';

import { CustomFieldTypes, REGISTRATION_FORM, RegistrationStatus } from '../constants';

import { isPayOnSiteSelectedStore, isPayWithEpassiSelectedStore } from '../stores';

import { mainConditions } from '../selectors/mainConditions';
import { distanceSelector, raceSelector } from '../selectors/mappedData';
import { totalPrice } from '../selectors/prices/totalPrice';
import { PAYMENT_METHOD_CODES } from '../types/paymentMethods';
import { PaymentMethodEnum } from '../types/paymentMethods';
import { parseUrlParams } from '../utils/parseUrlParams';
import {
  CHECKOUT_HIDDEN_ABANDON_DELAY_MS,
  HIDDEN_ABANDON_DELAY_MS,
  REGISTRATION_ANALYTICS_ENABLED,
  RegistrationAnalyticsEvent as Event,
  RegistrationStage,
} from './constants';
import { joinFields, pathTemplate, priceBand, sanitizeParams } from './normalize';
import { reasonFromErrors, refineCapacityReason } from './reasonFromErrors';
import { sectionOf } from './sectionOf';
import {
  AnalyticsSession,
  CartSnapshot,
  classifyStoredSession,
  createSession,
  exitReason,
  isCompletedRegistration,
  markCompletedRegistration,
  readSession,
  removeSession,
  writeSession,
} from './session';

/**
 * Registration API paths whose 5xx / network failures count as registration system errors
 */
const REGISTRATION_ENDPOINTS = [
  /\/races\/\d+\/registrations/,
  /\/races\/\d+\/confirmations/,
  /\/races\/\d+\/transfers/,
  /\/races\/\d+\/change-distance/,
  /\/registrations\//,
  /\/payments?\//,
  /\/user\/register/,
  /\/user\/profile/,
  /\/distances\/\d+/,
  /\/races\/\d+$/,
];

let session: AnalyticsSession | null = null;
let hiddenTimer: number | null = null;
let leavingForRedirect = false;
let disposeRequestErrors: (() => void) | null = null;
let disposeFieldChanges: (() => void) | null = null;
let redirectReported = false;

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
}

function isEnabled(): boolean {
  return REGISTRATION_ANALYTICS_ENABLED && typeof window !== 'undefined' && typeof window.gtag === 'function';
}

function save() {
  if (!session) return;
  session.updatedAt = Date.now();
  writeSession(storage(), session);
}

function regType(): string {
  if (mainConditions.isEditRegistration()) return 'edit';
  if (mainConditions.isChangeDistance.get()) return 'change_distance';
  if (mainConditions.isTransferredRegistration.get()) return 'transfer';
  if (mainConditions.isConfirmation.get()) return confirmationType();
  if (mainConditions.isTeam.get()) return 'team';
  return participantsCount() > 1 ? 'group' : 'single';
}

/**
 * Invite links (invite_token) cover different cases:
 * - payment requested: the organizer asks an existing participant to pay (payment_required)
 * - added by organizer: the organizer added the participant and asks them to confirm (added_at)
 * - team member: a team leader invited the participant to join the team
 */
function confirmationType(): string {
  if (mainConditions.isPushPayment.get()) return 'confirmation_payment_request';
  if (mainConditions.isConfirmationRacerAddedManually.get()) return 'confirmation_added_by_organizer';
  if (mainConditions.isTeam.get()) return 'confirmation_team_member';
  return 'confirmation';
}

function participantsCount(): number {
  const key = mainConditions.isTeam.get() ? 'teamMembers' : 'groupRegistrations';
  const value = form.fetch<unknown[]>(REGISTRATION_FORM, key);

  return Array.isArray(value) ? value.length : 1;
}

function paymentMethod(): string {
  if (totalPrice.get() === 0) return 'free';
  if (isPayWithEpassiSelectedStore.value) return 'epassi';
  if (isPayOnSiteSelectedStore.value) return 'on_site';

  const code = form.fetch<number>(REGISTRATION_FORM, 'groupRegistrations.0.payment_method');
  if (code === PAYMENT_METHOD_CODES[PaymentMethodEnum.EPASSI]) return 'epassi';
  if (code === PAYMENT_METHOD_CODES[PaymentMethodEnum.PAY_ON_SITE]) return 'on_site';

  return 'online';
}

function isExtraCustomField(id: number): boolean {
  const customFields = distanceSelector.model.get()?.value.custom_fields || [];

  return customFields.some((field) => field.id === id && field.type === CustomFieldTypes.extra);
}

/**
 * The cart as it is now in the form
 */
function liveCart(): CartSnapshot {
  const value = totalPrice.get();

  return {
    value,
    currency: currency(),
    regType: regType(),
    paymentMethod: paymentMethod(),
    participantsCount: participantsCount(),
    priceBand: priceBand(value),
    items: items(),
  };
}

/**
 * The cart submitted at checkout when there is one, the form can be empty after a payment redirect
 */
function cart(): CartSnapshot {
  return session?.cart || liveCart();
}

function commonParams(): Record<string, unknown> {
  const now = Date.now();
  const race = raceSelector.model.get()?.value;
  const current = cart();

  return {
    checkout_id: session?.checkoutId,
    race_id: raceSelector.id.get() || session?.raceId,
    distance_id: distanceSelector.id.get(),
    sport_type: race?.sport?.title_en,
    reg_type: current.regType,
    is_logged_in: sessionStore.isAuth,
    participants_count: current.participantsCount,
    payment_method: current.paymentMethod,
    price_band: current.priceBand,
    stage: session?.stage,
    elapsed_ms: session ? now - session.startedAt : undefined,
    stage_ms: session ? now - session.stageAt : undefined,
  };
}

function send(name: string, params: Record<string, unknown> = {}) {
  if (!isEnabled()) return;

  try {
    const isProduction = appEnvControl.isEnv('production');

    window.gtag!('event', name, {
      ...sanitizeParams({ ...commonParams(), ...params }),
      send_to: GA_MEASUREMENT_ID,
      transport_type: 'beacon',
      ...(isProduction ? {} : { debug_mode: true, traffic_type: 'internal' }),
    });
  } catch (e) {
    console.error(e);
  }
}

function setStage(stage: RegistrationStage) {
  if (!session || session.stage === stage) return;
  session.stage = stage;
  session.stageAt = Date.now();
  save();
}

function setIssue(reason: string) {
  if (!session) return;
  session.issue = { reason, at: Date.now() };
  save();
}

function clearIssue() {
  if (!session) return;
  session.issue = null;
  save();
}

/**
 * Editing an existing registration is not a new registration, it is left out of the funnel
 */
function trackedRaceId(): number | null {
  const { raceId, editRacerId } = parseUrlParams();

  return raceId && !editRacerId ? Number(raceId) : null;
}

function ensureSession(): AnalyticsSession | null {
  if (session) return session;

  const raceId = trackedRaceId();
  if (!raceId) return null;

  session = createSession(raceId, Date.now());
  save();

  return session;
}

function currency(): string | undefined {
  return raceSelector.currency.get() || undefined;
}

function items() {
  const distanceId = distanceSelector.id.get();

  return distanceId ? [{ item_id: `distance_${distanceId}`, item_category: regType(), quantity: participantsCount() }] : [];
}

function entrySource(): string {
  const { smartLinkToken, inviteToken, changeDistanceToken } = parseUrlParams();

  if (smartLinkToken) return 'smart_link';
  if (inviteToken) return 'invite';
  if (changeDistanceToken) return 'change_distance';
  if (mainConditions.isTransferredRegistration.get()) return 'transfer';
  if (historyStore.length >= 2 && historyStore[historyStore.length - 2].pathname.includes('/about')) return 'race_page';
  if (historyStore.length <= 2) return 'direct';
  return 'other';
}

function utmParams(): Record<string, string | null> {
  const params = new URLSearchParams(window.location.search);

  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
  };
}

function abandon(trigger: string, nextPage?: string) {
  if (!session || session.completed || session.abandoned || leavingForRedirect) return;

  send(Event.abandoned, {
    trigger,
    next_page: nextPage,
    last_stage: session.stage,
    last_section: session.lastSection,
    exit_reason: exitReason(session),
  });

  session.abandoned = true;
  save();
}

function onPageHide() {
  abandon('page_closed');
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    const isPaying = session?.stage === 'checkout' || session?.stage === 'payment';
    hiddenTimer = window.setTimeout(() => abandon('tab_hidden'), isPaying ? CHECKOUT_HIDDEN_ABANDON_DELAY_MS : HIDDEN_ABANDON_DELAY_MS);
    return;
  }

  if (hiddenTimer) {
    window.clearTimeout(hiddenTimer);
    hiddenTimer = null;
  }

  resumeIfAbandoned('tab_return');
}

/**
 * @description
 * A reload cannot be told apart from closing the tab when the page unloads, so reg_abandoned is sent
 * either way and reg_resumed follows when the user is back. resume_cause tells why, so reports can
 * use net abandonment (reg_abandoned minus reg_resumed) or drop reloads only.
 */
function resumeIfAbandoned(cause: 'reload' | 'tab_return' | 'return') {
  if (!session || !session.abandoned || session.completed) return;

  session.abandoned = false;
  save();
  send(Event.resumed, { resume_cause: cause });
}

function isReload(): boolean {
  try {
    const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return navigation?.type === 'reload';
  } catch (e) {
    return false;
  }
}

function onRegistrationRequestError(info: RequestErrorInfo) {
  if (!session || !REGISTRATION_ENDPOINTS.some((pattern) => pattern.test(info.url))) return;

  let kind = 'http_5xx';
  if (info.status === 0) kind = 'network';
  if (info.status === 502 || info.status === 503) kind = 'unavailable';

  systemError(kind, `${info.method} ${pathTemplate(info.url)}`);
}

function onAppError() {
  systemError('js_exception');
}

/**
 * @description
 * Start tracking when the registration page mounts.
 * Restores the checkout from sessionStorage after a reload or a redirect (Odin, ePassi, Stripe).
 */
function start() {
  const raceId = trackedRaceId();
  if (!raceId) return;

  const now = Date.now();
  const stored = readSession(storage(), raceId);
  leavingForRedirect = false;
  redirectReported = false;

  switch (classifyStoredSession(stored, now)) {
    case 'resume':
      session = stored;
      break;
    case 'authDropoff':
      session = stored;
      abandon('auth_not_returned');
      session = createSession(raceId, now);
      break;
    default:
      session = createSession(raceId, now);
  }

  save();

  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibilityChange);
  disposeRequestErrors = onRequestError(onRegistrationRequestError);
  disposeFieldChanges = onUserFieldChange(formChanged);
  window.addEventListener(APP_ERROR_EVENT, onAppError);
}

/**
 * @description
 * Registration page data is loaded
 */
function viewed() {
  if (!ensureSession()) return;
  const current = session!;

  if (current.authPending) {
    send(Event.authReturned, { mode: current.authPending.mode, auth_success: sessionStore.isAuth });
    current.authPending = null;
    setStage(current.formStarted ? 'form' : 'viewed');
    save();
  }

  if (current.abandoned) {
    resumeIfAbandoned(isReload() ? 'reload' : 'return');
    return;
  }

  if (!current.viewedSent) {
    current.viewedSent = true;
    save();
    send(Event.viewed, { source: entrySource(), ...utmParams() });
  }
}

/**
 * @description
 * Registration page unmounts (route change inside the app)
 */
function stop() {
  window.removeEventListener('pagehide', onPageHide);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  disposeRequestErrors?.();
  disposeRequestErrors = null;
  disposeFieldChanges?.();
  disposeFieldChanges = null;
  window.removeEventListener(APP_ERROR_EVENT, onAppError);

  if (hiddenTimer) {
    window.clearTimeout(hiddenTimer);
    hiddenTimer = null;
  }

  if (session && !session.completed) {
    abandon('navigated_away', pathTemplate(history.location.pathname));
  }

  session = null;
}

/**
 * @description
 * The page is about to be left on purpose (Odin, ePassi, Stripe redirect, restart), do not count it as abandonment
 */
function leavingPage() {
  leavingForRedirect = true;
}

function cancelLeavingPage() {
  leavingForRedirect = false;
}

function blocked(reason: string) {
  if (!ensureSession()) return;
  if (session!.blockedSent.includes(reason)) return;

  session!.blockedSent.push(reason);
  setIssue(`blocked:${reason}`);
  send(Event.blocked, { reason_code: reason });
}

function authRedirect(mode: string) {
  if (!ensureSession()) return;

  session!.authPending = { mode, at: Date.now() };
  setStage('auth');
  send(Event.authRedirect, { mode });
  leavingPage();
}

function formChanged(formId: string, path: string | string[]) {
  if (formId !== REGISTRATION_FORM || !ensureSession()) return;
  const current = session!;

  const section = sectionOf(path, isExtraCustomField);
  current.lastSection = section;
  // The user is changing the cart again (e.g. after a cancelled payment), report it live until the next checkout
  current.cart = null;

  if (!current.formStarted) {
    current.formStarted = true;
    setStage('form');
    send(Event.formStarted, { section });
  }

  if (!current.sectionsStarted.includes(section)) {
    current.sectionsStarted.push(section);
    send(Event.sectionStarted, { section });
  }

  save();
}

function productViewed(productId: number) {
  if (!ensureSession()) return;
  send(Event.productViewed, { product_id: productId });
}

function productAdded(productId: number, quantity: number) {
  if (!ensureSession()) return;
  send(Event.productAdded, { product_id: productId, quantity });
}

/**
 * @description
 * Every press of the form submit button, including "continue" steps and clicks while the form is incomplete
 */
function submitClicked(params: { blocked: boolean; step: string | nil }) {
  if (!ensureSession()) return;
  send(Event.submitClicked, { blocked: params.blocked, step: params.step });
}

/**
 * @description
 * Validation passed and the registration is being sent to the backend
 */
function submitting() {
  if (!ensureSession()) return;
  setStage('submit');
}

function validationFailed(errorKeys: string[]) {
  if (!ensureSession()) return;

  // Flattened error keys end with the message index (groupRegistrations.0.email.0), one field can have several messages
  const fieldKeys = Array.from(new Set(errorKeys.map((key) => key.replace(/\.\d+$/, ''))));
  const sections = Array.from(new Set(fieldKeys.map((key) => sectionOf(key, isExtraCustomField))));
  setIssue('validation_unresolved');
  send(Event.validationFailed, {
    fields: joinFields(fieldKeys),
    sections: sections.join(','),
    error_count: fieldKeys.length,
  });
}

/**
 * @description
 * The registration POST was rejected.
 * 5xx and network errors are reported as reg_system_error from the ResponseProcessor instead.
 * reloads resolves when race and distance have been reloaded, it is used to tell sold out from closed.
 */
async function submitRejected(status: number | nil, errors: RawError | nil, reloads?: Promise<unknown>) {
  if (!ensureSession()) return;
  if (!status || status >= 500) return;

  let { reason, fields } = reasonFromErrors(status, errors) as { reason: string; fields: string };

  if (reason === 'capacity_or_closed' && reloads) {
    await reloads.catch(() => null);
    reason = refineCapacityReason(mainConditions.isDistanceSoldOut.get(), mainConditions.isRegistrationClosed.get());
  }

  setIssue(`submit_rejected:${reason}`);
  send(Event.submitRejected, { reason_code: reason, http_status: status, fields });
}

function systemError(kind: string, endpoint?: string) {
  if (!ensureSession()) return;
  setIssue(`system_error:${kind}`);
  send(Event.systemError, { error_kind: kind, endpoint });
}

/**
 * @description
 * The backend accepted the submit and returned a registration
 */
function checkoutBegun(registrationUuid: string | nil) {
  if (!ensureSession()) return;

  const snapshot = liveCart();

  session!.registrationUuid = registrationUuid || null;
  session!.cart = snapshot;
  clearIssue();
  setStage('checkout');
  send(Event.beginCheckout, { currency: snapshot.currency, value: snapshot.value, items: snapshot.items });
}

function paymentInfoAdded() {
  if (!ensureSession()) return;
  setStage('payment');
  send(Event.addPaymentInfo, { currency: cart().currency, value: cart().value });
}

function paymentFailed(error: { type?: string; code?: string; decline_code?: string } | nil) {
  if (!ensureSession()) return;

  const code = error?.decline_code || error?.code || error?.type || 'unknown';
  setIssue(`payment_declined:${code}`);
  setStage('checkout');
  send(Event.paymentFailed, { stripe_type: error?.type, stripe_code: error?.code, decline_code: error?.decline_code });
}

function paymentProcessing() {
  if (!ensureSession()) return;
  send(Event.paymentProcessing);
}

function paymentCancelled() {
  if (!ensureSession()) return;
  // The frame close and the payment observer can both report the same cancel
  if (session!.issue?.reason === 'payment_cancelled' && Date.now() - session!.issue.at < 5000) return;

  setIssue('payment_cancelled');
  send(Event.paymentCancelled);
  setStage('checkout');
}

function checkoutExpired() {
  if (!ensureSession()) return;
  setIssue('checkout_expired');
  send(Event.checkoutExpired);
  setStage('checkout');
}

function checkoutRestarted() {
  if (!ensureSession()) return;
  send(Event.checkoutRestarted);
  leavingPage();
}

function redirectReturned(paymentStatus: string | nil, polledStatus: string | nil) {
  if (!ensureSession() || redirectReported) return;
  redirectReported = true;
  send(Event.redirectReturned, { payment_status: paymentStatus, polled_status: polledStatus });
}

function statusUnresolved(polls: number) {
  if (!ensureSession()) return;
  setIssue('status_unresolved');
  send(Event.statusUnresolved, { polls });
}

/**
 * @description
 * The success screen is shown. Sent once per registration.
 * confirmed: the backend already returned status completed. When it did not (pay on site, free and
 * card payments complete from a queued job), the status is polled in the background and
 * reg_completion_failed / reg_status_unresolved is sent if it never completes.
 *
 * Funnel signal only: reg_completed and the GA4 purchase revenue are NOT the official Registration or
 * GTV metrics (confirmed, paid, excluding cancellations and refunds). Report those from backend data.
 */
function completed(registrationUuid?: string | nil, confirmed: boolean = false) {
  if (!ensureSession()) return;
  const current = session!;

  const uuid = registrationUuid || current.registrationUuid;
  const transactionId = uuid || current.checkoutId;

  if (current.completed || isCompletedRegistration(storage(), transactionId)) return;

  const { currency: cartCurrency, value, items: cartItems } = cart();
  const params = { currency: cartCurrency, value, transaction_id: transactionId };

  current.completed = true;
  setStage('completed');
  send(Event.completed, { ...params, backend_confirmed: confirmed });
  send(Event.purchase, { ...params, items: cartItems });
  markCompletedRegistration(storage(), transactionId);
  removeSession(storage(), current.raceId);

  if (!confirmed && uuid) {
    confirmInBackground(uuid, current.checkoutId);
  }
}

const CONFIRM_FIRST_DELAY_MS = 2000;
const CONFIRM_MAX_DELAY_MS = 15000;
const CONFIRM_BUDGET_MS = 2 * 60 * 1000;

/**
 * @description
 * Background check that a registration shown as successful is really completed by the backend.
 * Backs off from 2s to 15s (about 11 requests in 2 minutes) and does not poll while the tab is hidden.
 */
async function confirmInBackground(uuid: string, checkoutId: string) {
  const startedAt = Date.now();
  let delay = CONFIRM_FIRST_DELAY_MS;
  let polls = 0;

  while (Date.now() - startedAt < CONFIRM_BUDGET_MS) {
    await new Promise((resolve) => window.setTimeout(resolve, delay));
    delay = Math.min(Math.round(delay * 1.5), CONFIRM_MAX_DELAY_MS);

    if (document.visibilityState === 'hidden') {
      continue;
    }

    polls += 1;

    // Plain request: the decorated service would show toasts and loaders for a background check
    const status = await axios
      .get(generatePath(API_URL + API_REGISTRATION_STATUS, { uuid }))
      .then((response) => response.data?.data?.status)
      .catch(() => null);

    if (status === RegistrationStatus.completed) {
      return;
    }

    if (status === RegistrationStatus.canceled) {
      send(Event.completionFailed, { checkout_id: checkoutId, transaction_id: uuid, polls });
      return;
    }
  }

  send(Event.statusUnresolved, { checkout_id: checkoutId, transaction_id: uuid, polls });
}

const registrationAnalytics = {
  start,
  viewed,
  stop,
  leavingPage,
  cancelLeavingPage,
  blocked,
  authRedirect,
  formChanged,
  productViewed,
  productAdded,
  submitClicked,
  submitting,
  validationFailed,
  submitRejected,
  systemError,
  checkoutBegun,
  paymentInfoAdded,
  paymentFailed,
  paymentProcessing,
  paymentCancelled,
  checkoutExpired,
  checkoutRestarted,
  redirectReturned,
  statusUnresolved,
  completed,
};

export { registrationAnalytics };
